<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import AnalyticsPanel from '../analytics/AnalyticsPanel.vue';
import MetricValue from '../analytics/MetricValue.vue';
import ProportionBar from '../analytics/ProportionBar.vue';
import { useAnalyticsFormat } from '../analytics/useAnalyticsFormat';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CaminhoConversas from './CaminhoConversas.vue';
import { reasonLabel } from './atendimentoReasons';
import { teamLabel } from './atendimentoLabels';
import { QUEM_CORES } from './quemCores';

// "O caminho das conversas": de tudo o que chegou no período, quanto a IA
// atendeu e quanto chegou sem ela — os dois somam 100% de "Chegaram" —, e, do
// que a IA atendeu, para onde cada conversa foi: os quatro baldes somam 100% de
// "A IA atendeu" (o servidor já manda os percentuais que somam 100). As barras
// são sobre "Chegaram". Cada linha abre a lista das conversas dela.
//
// "Sem IA" se abre em duas, que somam o total dele: a caixa sem agente e a IA que
// não respondeu. O recorte de disparo (as que começaram respondendo a um disparo)
// não tira nada de linha nenhuma: a conversa de disparo que a IA atendeu continua
// sendo da IA, e cada linha só diz quantas das dela vieram de disparo.
const props = defineProps({
  // A resposta de GET atendimento/caminho; null enquanto não chegou.
  report: { type: Object, default: null },
  failed: { type: Boolean, default: false },
  // A resposta de GET atendimento/ferramentas, que traz "… e a IA concluiu uma
  // tarefa" — do runtime, à parte, para a árvore não esperar por ele.
  tasks: { type: Object, default: null },
  tasksFailed: { type: Boolean, default: false },
  // O recorte da página, para a lista pedir as mesmas conversas.
  filters: { type: Object, default: () => ({}) },
  rangeLabel: { type: String, default: '' },
});

// O filtro "Origem" (client | outbound) e o "Abrir por" (agent | inbox | team |
// user): moram na URL, e a página pede o Caminho de novo quando mudam. null é
// "todas" e "só o total".
const origin = defineModel('origin', { type: String, default: null });
const by = defineModel('by', { type: String, default: null });

const { t } = useI18n();
const { number, percent, duration } = useAnalyticsFormat();

const listRef = ref(null);

// Nada chegou: a árvore inteira vira "sem dado". A IA não atender nada não é
// isso — "Chegaram" e "Sem IA" seguem, e só os galhos da IA somem.
const noConversationsReason = computed(() =>
  props.report?.no_data_reason === 'no_conversations'
    ? reasonLabel(t, props.report.no_data_reason)
    : ''
);
const arrived = computed(() => props.report?.arrived ?? null);
const attended = computed(() => props.report?.ai_attended ?? null);
const buckets = computed(() => props.report?.buckets || {});
const after = computed(() => props.report?.after_handoff || null);
const afterReason = computed(() =>
  after.value?.reason ? reasonLabel(t, after.value.reason) : ''
);

const task = computed(() => props.tasks?.task_completed || null);

// Das conversas de cada linha, as que começaram por disparo. Zero não aparece, e
// com a origem escolhida o recorte é o próprio filtro: não aparece também.
const fromOutbound = computed(() =>
  origin.value ? null : props.report?.from_outbound || null
);
const outboundOf = count =>
  typeof count === 'number' && count > 0
    ? t('ATENDIMENTO_REPORTS.PATH.FROM_OUTBOUND', { count: number(count) })
    : '';
const outboundBucket = key => outboundOf(fromOutbound.value?.buckets?.[key]);

const repliedLabel = computed(() => {
  const p50 = after.value?.first_reply_seconds?.p50;
  return typeof p50 === 'number'
    ? t('ATENDIMENTO_REPORTS.PATH.REPLIED_MEDIAN', { time: duration(p50) })
    : t('ATENDIMENTO_REPORTS.PATH.REPLIED');
});

// As linhas da árvore, na ordem do desenho. `level` recua a linha; `fill` é a
// classe da barra (escrita por inteiro: o Tailwind só gera o que lê no fonte).
// A IA e a pessoa pintam pelo mapa único, quemCores.js.
// Os galhos da IA só existem quando ela atendeu alguma conversa.
const aiLines = computed(() => [
  {
    key: 'terminou_sem_pessoa',
    level: 2,
    label: t('ATENDIMENTO_REPORTS.PATH.FINISHED'),
    count: buckets.value.terminou_sem_pessoa?.count,
    share: buckets.value.terminou_sem_pessoa?.percent,
    outbound: outboundBucket('terminou_sem_pessoa'),
    fill: 'fill-n-teal-9',
  },
  {
    key: 'tarefa',
    level: 3,
    label: t('ATENDIMENTO_REPORTS.PATH.TASK'),
    count: task.value?.count ?? null,
    loading: !props.tasks && !props.tasksFailed,
    reason: reasonLabel(
      t,
      props.tasksFailed ? 'load_failed' : task.value?.reason
    ),
    // A lista que o runtime cortou faz do número um piso: a tela diz isso.
    detail: task.value?.truncated
      ? t('ATENDIMENTO_REPORTS.PATH.TASK_TRUNCATED')
      : '',
    fill: 'fill-n-teal-7',
  },
  {
    key: 'passou',
    level: 2,
    label: t('ATENDIMENTO_REPORTS.PATH.HANDED_OFF'),
    count: buckets.value.passou?.count,
    share: buckets.value.passou?.percent,
    outbound: outboundBucket('passou'),
    fill: 'fill-n-amber-9',
  },
  {
    key: 'respondeu',
    level: 3,
    label: repliedLabel.value,
    count: after.value?.replied?.count,
    share: after.value?.replied?.percent,
    reason: afterReason.value,
    fill: QUEM_CORES.pessoa.fill,
  },
  {
    key: 'resolveram',
    level: 4,
    label: t('ATENDIMENTO_REPORTS.PATH.RESOLVED'),
    count: after.value?.resolved,
    reason: afterReason.value,
    fill: 'fill-n-teal-9',
  },
  {
    key: 'devolveram',
    level: 4,
    label: t('ATENDIMENTO_REPORTS.PATH.RETURNED'),
    count: after.value?.returned_to_ai,
    reason: afterReason.value,
    fill: QUEM_CORES.ia.fill,
  },
  {
    key: 'ainda_com_equipe',
    level: 4,
    label: t('ATENDIMENTO_REPORTS.PATH.STILL_WITH_TEAM'),
    count: after.value?.still_with_team,
    reason: afterReason.value,
    fill: 'fill-n-slate-9',
  },
  {
    key: 'ninguem_respondeu',
    level: 3,
    label: t('ATENDIMENTO_REPORTS.PATH.UNANSWERED'),
    count: after.value?.unanswered?.count,
    share: after.value?.unanswered?.percent,
    reason: afterReason.value,
    fill: 'fill-n-ruby-9',
    alert: true,
  },
  {
    key: 'pessoa_sem_pedido',
    level: 2,
    label: t('ATENDIMENTO_REPORTS.PATH.PERSON_JOINED'),
    count: buckets.value.pessoa_sem_pedido?.count,
    share: buckets.value.pessoa_sem_pedido?.percent,
    outbound: outboundBucket('pessoa_sem_pedido'),
    fill: QUEM_CORES.pessoa.fill,
  },
  {
    key: 'em_aberto_com_ia',
    level: 2,
    label: t('ATENDIMENTO_REPORTS.PATH.OPEN_WITH_AI'),
    count: buckets.value.em_aberto_com_ia?.count,
    share: buckets.value.em_aberto_com_ia?.percent,
    outbound: outboundBucket('em_aberto_com_ia'),
    fill: 'fill-n-slate-8',
  },
]);

// As duas partes de "Sem IA", quando o servidor as manda.
const withoutAiParts = computed(() => {
  const withoutAi = props.report?.without_ai;
  if (typeof withoutAi?.no_agent_inbox !== 'number') return [];
  return [
    {
      key: 'sem_agente',
      level: 2,
      label: t('ATENDIMENTO_REPORTS.PATH.NO_AGENT_INBOX'),
      count: withoutAi.no_agent_inbox,
      fill: 'fill-n-slate-6',
    },
    {
      key: 'ia_nao_respondeu',
      level: 2,
      label: t('ATENDIMENTO_REPORTS.PATH.AI_DID_NOT_REPLY'),
      count: withoutAi.ai_did_not_reply,
      fill: 'fill-n-slate-6',
    },
  ];
});

const lines = computed(() => [
  {
    key: 'chegaram',
    level: 0,
    label: t('ATENDIMENTO_REPORTS.PATH.ARRIVED'),
    count: arrived.value,
    outbound:
      fromOutbound.value?.arrived > 0
        ? t(
            'ATENDIMENTO_REPORTS.PATH.FROM_OUTBOUND_ARRIVED',
            { count: number(fromOutbound.value.arrived) },
            fromOutbound.value.arrived
          )
        : '',
    fill: 'fill-n-slate-11',
  },
  {
    key: 'atendeu',
    level: 1,
    label: t('ATENDIMENTO_REPORTS.PATH.AI_ATTENDED'),
    count: attended.value,
    share: props.report?.ai_attended_percent,
    outbound: outboundOf(fromOutbound.value?.ai_attended),
    fill: QUEM_CORES.ia.fill,
  },
  ...(props.report?.buckets ? aiLines.value : []),
  {
    key: 'sem_ia',
    level: 1,
    label: t('ATENDIMENTO_REPORTS.PATH.WITHOUT_AI'),
    count: props.report?.without_ai?.count,
    share: props.report?.without_ai?.percent,
    outbound: outboundOf(fromOutbound.value?.without_ai),
    fill: 'fill-n-slate-6',
  },
  ...withoutAiParts.value,
]);

const ORIGIN_OPTIONS = computed(() => [
  { key: null, label: t('ATENDIMENTO_REPORTS.PATH.ORIGIN.ALL') },
  { key: 'client', label: t('ATENDIMENTO_REPORTS.PATH.ORIGIN.CLIENT') },
  { key: 'outbound', label: t('ATENDIMENTO_REPORTS.PATH.ORIGIN.OUTBOUND') },
]);
// O título da tabela é frase ("O caminho por caixa"): um texto por dimensão, e
// não o rótulo do botão, que vem com maiúscula.
const OPEN_BY_OPTIONS = computed(() => [
  { key: null, label: t('ATENDIMENTO_REPORTS.PATH.OPEN_BY.TOTAL') },
  {
    key: 'agent',
    label: t('ATENDIMENTO_REPORTS.CHANNELS.BY.AGENT'),
    title: t('ATENDIMENTO_REPORTS.PATH.OPEN_BY.TITLE.AGENT'),
  },
  {
    key: 'inbox',
    label: t('ATENDIMENTO_REPORTS.CHANNELS.BY.INBOX'),
    title: t('ATENDIMENTO_REPORTS.PATH.OPEN_BY.TITLE.INBOX'),
  },
  {
    key: 'team',
    label: t('ATENDIMENTO_REPORTS.CHANNELS.BY.TEAM'),
    title: t('ATENDIMENTO_REPORTS.PATH.OPEN_BY.TITLE.TEAM'),
  },
  {
    key: 'user',
    label: t('ATENDIMENTO_REPORTS.CHANNELS.BY.USER'),
    title: t('ATENDIMENTO_REPORTS.PATH.OPEN_BY.TITLE.USER'),
  },
]);
const selectors = computed(() => [
  {
    key: 'origem',
    label: t('ATENDIMENTO_REPORTS.PATH.ORIGIN.LABEL'),
    options: ORIGIN_OPTIONS.value,
    model: origin,
  },
  {
    key: 'abrir',
    label: t('ATENDIMENTO_REPORTS.PATH.OPEN_BY.LABEL'),
    options: OPEN_BY_OPTIONS.value,
    model: by,
  },
]);

// "Abrir por": a mesma árvore por agente, caixa, time ou atendente, uma linha por
// grupo, ao lado da árvore do total. A chave nula é a das conversas sem um (a
// caixa sem agente, a conversa sem time ou sem responsável); o grupo sem nome é
// o que a conta apagou.
const groups = computed(() =>
  props.report?.by && Array.isArray(props.report.groups)
    ? props.report.groups
    : []
);
const groupName = group => {
  const dimension = props.report.by;
  if (group.key === null) {
    if (dimension === 'agent')
      return t('ATENDIMENTO_REPORTS.CHANNELS.NONE.AGENT');
    if (dimension === 'inbox')
      return t('ATENDIMENTO_REPORTS.CHANNELS.NONE.INBOX');
    if (dimension === 'team')
      return t('ATENDIMENTO_REPORTS.CHANNELS.NONE.TEAM');
    return t('ATENDIMENTO_REPORTS.CHANNELS.NONE.USER');
  }
  if (!group.name) {
    if (dimension === 'agent')
      return t('ATENDIMENTO_REPORTS.CHANNELS.REMOVED.AGENT');
    if (dimension === 'inbox')
      return t('ATENDIMENTO_REPORTS.CHANNELS.REMOVED.INBOX');
    if (dimension === 'team')
      return t('ATENDIMENTO_REPORTS.CHANNELS.REMOVED.TEAM');
    return t('ATENDIMENTO_REPORTS.CHANNELS.REMOVED.USER');
  }
  return dimension === 'team' ? teamLabel(group.name) : group.name;
};
// Os baldes da IA ficam nulos no grupo em que ela não atendeu nada.
const bucketOf = (group, key) => group.buckets?.[key]?.count ?? null;
const groupColumns = computed(() => [
  {
    key: 'chegaram',
    label: t('ATENDIMENTO_REPORTS.PATH.ARRIVED'),
    value: group => group.arrived,
  },
  {
    key: 'atendeu',
    label: t('ATENDIMENTO_REPORTS.PATH.AI_ATTENDED'),
    value: group => group.ai_attended,
    share: group => group.ai_attended_percent,
  },
  {
    key: 'terminou_sem_pessoa',
    label: t('ATENDIMENTO_REPORTS.PATH.FINISHED'),
    value: group => bucketOf(group, 'terminou_sem_pessoa'),
  },
  {
    key: 'passou',
    label: t('ATENDIMENTO_REPORTS.PATH.HANDED_OFF'),
    value: group => bucketOf(group, 'passou'),
  },
  {
    key: 'pessoa_sem_pedido',
    label: t('ATENDIMENTO_REPORTS.PATH.PERSON_JOINED'),
    value: group => bucketOf(group, 'pessoa_sem_pedido'),
  },
  {
    key: 'em_aberto_com_ia',
    label: t('ATENDIMENTO_REPORTS.PATH.OPEN_WITH_AI'),
    value: group => bucketOf(group, 'em_aberto_com_ia'),
  },
  {
    key: 'sem_ia',
    label: t('ATENDIMENTO_REPORTS.PATH.WITHOUT_AI'),
    value: group => group.without_ai?.count ?? null,
    share: group => group.without_ai?.percent,
  },
  ...(origin.value
    ? []
    : [
        {
          key: 'disparo',
          label: t('ATENDIMENTO_REPORTS.PATH.OPEN_BY.FROM_OUTBOUND'),
          value: group => group.from_outbound?.arrived ?? null,
        },
      ]),
]);

const INDENT = [
  'pl-0',
  'pl-4 sm:pl-6',
  'pl-8 sm:pl-12',
  'pl-12 sm:pl-[4.5rem]',
  'pl-16 sm:pl-24',
];

const hasCount = line => typeof line.count === 'number';
const clickable = line => hasCount(line) && line.count > 0;

const openList = line => {
  if (!clickable(line)) return;
  listRef.value?.open(line.key, line.label, line.count);
};
</script>

<template>
  <AnalyticsPanel
    data-test-id="atendimento-caminho"
    :title="t('ATENDIMENTO_REPORTS.PATH.TITLE')"
    :description="
      rangeLabel
        ? t('ATENDIMENTO_REPORTS.PATH.DESCRIPTION', { range: rangeLabel })
        : ''
    "
  >
    <template #aside>
      <div class="flex flex-col gap-2 items-start max-w-full sm:items-end">
        <div
          v-for="selector in selectors"
          :key="selector.key"
          class="flex flex-wrap gap-x-2 gap-y-1 items-center max-w-full"
        >
          <span class="text-xs text-n-slate-11">{{ selector.label }}</span>
          <div
            role="group"
            :aria-label="selector.label"
            class="flex flex-wrap p-0.5 max-w-full rounded-lg border border-n-weak bg-n-alpha-1"
          >
            <button
              v-for="option in selector.options"
              :key="String(option.key)"
              type="button"
              :data-test-id="`atendimento-${selector.key}-${option.key || 'todas'}`"
              :aria-pressed="selector.model.value === option.key"
              class="px-2.5 py-1 text-xs font-medium rounded-md transition-colors"
              :class="
                selector.model.value === option.key
                  ? 'bg-n-solid-3 text-n-slate-12 shadow-sm'
                  : 'text-n-slate-11 hover:text-n-slate-12'
              "
              @click="selector.model.value = option.key"
            >
              {{ option.label }}
            </button>
          </div>
        </div>
      </div>
    </template>

    <p v-if="failed" class="mb-0 text-sm text-n-ruby-11">
      {{ t('ATENDIMENTO_REPORTS.FAILED') }}
    </p>

    <MetricValue
      v-else-if="noConversationsReason"
      data-test-id="atendimento-caminho-no-data"
      :reason="noConversationsReason"
    />

    <ul v-else-if="report" class="flex flex-col list-none">
      <li v-for="line in lines" :key="line.key" class="min-w-0">
        <button
          type="button"
          :data-test-id="`atendimento-caminho-${line.key}`"
          :disabled="!clickable(line)"
          class="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-1 items-center py-1.5 w-full text-start rounded-md sm:grid-cols-[minmax(0,1fr)_minmax(6rem,14rem)_5.5rem] enabled:hover:bg-n-alpha-1"
          :class="INDENT[line.level]"
          @click="openList(line)"
        >
          <span class="flex flex-col min-w-0">
            <span
              class="text-sm break-words"
              :class="[
                line.level === 0 ? 'font-medium' : '',
                line.alert && line.count ? 'text-n-ruby-11' : 'text-n-slate-12',
              ]"
            >
              {{ line.label }}
            </span>
            <span v-if="line.detail" class="text-xs text-n-slate-10">
              {{ line.detail }}
            </span>
            <span
              v-if="line.outbound"
              :data-test-id="`atendimento-caminho-${line.key}-outbound`"
              class="flex gap-1.5 items-center text-xs text-n-slate-10"
            >
              <span
                class="flex-shrink-0 rounded-full size-1.5"
                :class="QUEM_CORES.disparo.bg"
              />
              {{ line.outbound }}
            </span>
            <span
              v-if="!hasCount(line) && !line.loading && line.reason"
              class="text-xs text-n-slate-10"
            >
              {{ line.reason }}
            </span>
          </span>
          <ProportionBar
            v-if="hasCount(line) && arrived"
            class="order-last col-span-2 h-2 sm:order-none sm:col-span-1"
            :segments="[{ key: line.key, value: line.count, fill: line.fill }]"
            :total="arrived"
          />
          <span v-else class="hidden sm:block" />
          <span
            class="text-sm whitespace-nowrap text-end tabular-nums"
            :data-test-id="`atendimento-caminho-${line.key}-value`"
          >
            <template v-if="hasCount(line)">
              <span class="font-medium text-n-slate-12">
                {{ number(line.count) }}
              </span>
              <span
                v-if="typeof line.share === 'number'"
                :data-test-id="`atendimento-caminho-${line.key}-share`"
                class="ms-1.5 text-xs text-n-slate-11"
              >
                {{ percent(line.share) }}
              </span>
            </template>
            <Spinner
              v-else-if="line.loading"
              :size="14"
              class="inline-flex text-n-slate-10"
            />
            <span v-else class="text-n-slate-10">
              {{ t('ANALYTICS_REPORTS.NO_DATA') }}
            </span>
          </span>
        </button>
      </li>
    </ul>

    <section
      v-if="!failed && groups.length"
      data-test-id="atendimento-abrir-grupos"
      class="flex flex-col gap-2 min-w-0"
    >
      <h3 class="text-sm font-medium text-n-slate-12">
        {{ OPEN_BY_OPTIONS.find(option => option.key === report.by)?.title }}
      </h3>
      <!-- Oito números por grupo: a tabela rola sozinha, e não a página. -->
      <div class="overflow-x-auto max-w-full">
        <table class="w-full text-sm min-w-[44rem]">
          <thead>
            <tr class="text-xs text-n-slate-11">
              <th class="py-2 pe-3 font-medium text-start">
                {{
                  OPEN_BY_OPTIONS.find(option => option.key === report.by)
                    ?.label
                }}
              </th>
              <th
                v-for="column in groupColumns"
                :key="column.key"
                class="py-2 ps-3 font-medium text-end"
              >
                {{ column.label }}
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-n-weak">
            <tr
              v-for="group in groups"
              :key="String(group.key)"
              :data-test-id="`atendimento-abrir-grupo-${group.key ?? 'nenhum'}`"
            >
              <td class="py-2 pe-3 font-medium text-n-slate-12">
                {{ groupName(group) }}
              </td>
              <td
                v-for="column in groupColumns"
                :key="column.key"
                :data-test-id="`atendimento-abrir-${column.key}`"
                class="py-2 ps-3 tabular-nums text-end text-n-slate-12"
              >
                <template v-if="typeof column.value(group) === 'number'">
                  {{ number(column.value(group)) }}
                  <span
                    v-if="
                      column.share && typeof column.share(group) === 'number'
                    "
                    class="block text-xs text-n-slate-11"
                  >
                    {{ percent(column.share(group)) }}
                  </span>
                </template>
                <span v-else class="text-xs text-n-slate-10">
                  {{ t('ANALYTICS_REPORTS.NO_DATA') }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <CaminhoConversas ref="listRef" :filters="filters" :origin="origin" />
  </AnalyticsPanel>
</template>
